import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { hikingPath, hikingUrl } from '../utils/paths';
import { normalizeClubSport } from '../config/clubSports';
import {
  formatDistance,
  formatElevation,
  formatTrailDifficulty,
} from '../config/hikerProfileOptions';
import { useMyClubMemberships, getMembershipStatusForClub } from './MembershipStatusBanner';
import './HikingClubHome.css';

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBA';
  return new Date(dateStr).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const HikingClubHome = () => {
  const { id } = useParams();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [trails, setTrails] = useState([]);
  const [outings, setOutings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canManage, setCanManage] = useState(false);
  const { memberships } = useMyClubMemberships(isAuthenticated ? user?.id : null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const clubData = await api.getClubById(id);
      setClub(clubData);
      if (normalizeClubSport(clubData.sport) !== 'HIKING') {
        return;
      }
      const [trailData, outingData] = await Promise.all([
        api.getHikingTrails(id, true),
        api.getUpcomingHikingOutings(id, 8),
      ]);
      setTrails(Array.isArray(trailData) ? trailData : []);
      setOutings(Array.isArray(outingData) ? outingData : []);
      const isOwner = user?.id && clubData.adminUserId === user.id;
      setCanManage(!!(isOwner || isAdmin()));
    } catch (err) {
      setError(err.message || 'Failed to load hiking club');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="loading">Loading club...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!club) {
    return <div className="error-message">Club not found</div>;
  }

  if (normalizeClubSport(club.sport) !== 'HIKING') {
    return <Navigate to={`/soccer/clubs/${id}`} replace />;
  }

  const membership = getMembershipStatusForClub(memberships, id);

  return (
    <div className="hiking-club-home">
      <header className="hiking-club-home__hero">
        <div
          className="hiking-club-home__hero-bg"
          style={club.imageUrl ? { backgroundImage: `url(${club.imageUrl})` } : undefined}
          aria-hidden="true"
        />
        <div className="hiking-club-home__hero-shade" aria-hidden="true" />
        <div className="hiking-club-home__hero-content">
          <p className="hiking-club-home__eyebrow">Hiking club</p>
          <h1>{club.name}</h1>
          {club.description && <p className="hiking-club-home__desc">{club.description}</p>}
          <div className="hiking-club-home__actions">
            <Link to={hikingPath(`/clubs/${id}/trails`)} className="btn btn-secondary">
              Browse trails
            </Link>
            <Link to={hikingPath(`/clubs/${id}/outings`)} className="btn btn-primary">
              Group outings
            </Link>
            {canManage && (
              <Link to={hikingPath(`/clubs/${id}/admin`)} className="btn btn-secondary">
                Club admin
              </Link>
            )}
          </div>
          {membership?.status === 'PENDING' && (
            <p className="hiking-club-home__note">Your join request is pending approval.</p>
          )}
        </div>
      </header>

      <section className="hiking-club-home__section">
        <div className="hiking-club-home__section-head">
          <h2>Upcoming outings</h2>
          <p>Vote availability and meet at the published trailhead.</p>
        </div>
        {outings.length === 0 ? (
          <p className="hiking-club-home__empty">No upcoming hikes yet.</p>
        ) : (
          <div className="hiking-club-home__list">
            {outings.map((outing) => (
              <article key={outing.id} className="hiking-outing-card">
                <div>
                  <h3>{outing.title}</h3>
                  <p className="hiking-outing-card__meta">{formatDate(outing.outingDate)}</p>
                  {outing.meetingLocation && (
                    <p className="hiking-outing-card__meta">Meet: {outing.meetingLocation}</p>
                  )}
                  {outing.trail?.title && (
                    <p className="hiking-outing-card__meta">Trail: {outing.trail.title}</p>
                  )}
                </div>
                <div className="hiking-outing-card__actions">
                  <Link to={hikingPath(`/outings/${outing.id}`)} className="btn btn-primary">
                    Vote &amp; details
                  </Link>
                  {outing.shareToken && (
                    <a
                      href={hikingUrl(`/public/outings/${outing.shareToken}`)}
                      className="btn btn-secondary"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Share link
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="hiking-club-home__section">
        <div className="hiking-club-home__section-head">
          <h2>Published trails</h2>
          <p>Pick a trail for an outing — organizers publish distance, climb, and map links.</p>
        </div>
        {trails.length === 0 ? (
          <p className="hiking-club-home__empty">No published trails yet.</p>
        ) : (
          <div className="hiking-club-home__trails">
            {trails.map((trail) => (
              <article key={trail.id} className="hiking-trail-card">
                <h3>{trail.title}</h3>
                {trail.distanceKm != null && (
                  <p className="hiking-trail-card__stat">{formatDistance(trail.distanceKm)}</p>
                )}
                {trail.elevationGainM != null && (
                  <p className="hiking-trail-card__meta">{formatElevation(trail.elevationGainM)}</p>
                )}
                <p className="hiking-trail-card__meta">
                  {[trail.difficulty && formatTrailDifficulty(trail.difficulty), trail.terrain]
                    .filter(Boolean)
                    .join(' · ') || 'Trail'}
                </p>
                {trail.trailheadLocation && (
                  <p className="hiking-trail-card__meta">Trailhead: {trail.trailheadLocation}</p>
                )}
                {trail.mapUrl && (
                  <a href={trail.mapUrl} target="_blank" rel="noreferrer">
                    Open map
                  </a>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default HikingClubHome;
